import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { useWatchHistoryStore } from '@jwp/ott-common/src/stores/WatchHistoryStore';
import { useSeries } from '@jwp/ott-hooks-react/src/series/useSeries';
import { useEpisodes } from '@jwp/ott-hooks-react/src/series/useEpisodes';
import { useSeriesLookup } from '@jwp/ott-hooks-react/src/series/useSeriesLookup';
import { useFirstEpisode } from '@jwp/ott-hooks-react/src/series/useFirstEpisode';
import { getFiltersFromSeries } from '@jwp/ott-common/src/utils/series';
import { useNextEpisode } from '@jwp/ott-hooks-react/src/series/useNextEpisode';
import { createVideoMetadata, createVideoFormatMetadata } from '@jwp/ott-common/src/utils/metadata';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import useMedia from '@jwp/ott-hooks-react/src/useMedia';
import useEntitlement from '@jwp/ott-hooks-react/src/useEntitlement';
import usePlaylist from '@jwp/ott-hooks-react/src/usePlaylist';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { mediaURL } from '@jwp/ott-common/src/utils/urlFormatting';
import { VideoProgressMinMax } from '@jwp/ott-common/src/constants';

export default function useSeriesData(data: PlaylistItem, seriesId: string, episodeIdParam: string | null) {
  const { t } = useTranslation('common');

  // Config
  const { config, accessModel } = useConfigStore(({ config, accessModel }) => ({ config, accessModel }), shallow);
  const { features } = config;
  const isFavoritesEnabled: boolean = Boolean(features?.favoritesList);

  // Series
  const { isLoading: isSeriesDataLoading, data: series } = useSeries(seriesId);
  const { data: firstEpisode, isLoading: isFirstEpisodeLoading } = useFirstEpisode({ series });
  const { isLoading: isRecommendationsLoading, data: recommendations } = usePlaylist(features?.recommendationsPlaylist || '', {
    related_media_id: seriesId,
    media_filtering: `contentType:${data.contentType}`, // nice to have: object notation for filters
  });

  // Watch history
  const watchHistoryArray = useWatchHistoryStore((state) => state.watchHistory);
  const watchHistoryDictionary = useWatchHistoryStore((state) => state.getDictionaryWithEpisodes());
  const episodeInProgress = watchHistoryArray.find(
    (episode) =>
      episode?.seriesId === seriesId &&
      (episode.progress > VideoProgressMinMax.Min || (episode.mediaid && episode.mediaid !== firstEpisode?.mediaid)) &&
      episode.progress < VideoProgressMinMax.Max,
  );

  // Episode (Deeplinked || watch progress || first episode)
  const initialEpisodeId = episodeIdParam || episodeInProgress?.mediaid;
  const [episodeId, setEpisodeId] = useState<string | null>(initialEpisodeId || firstEpisode?.mediaid || null);
  const { isLoading: isEpisodeLoading, data: episode } = useMedia(episodeId || '');
  const { isLoading: isSeriesDictionaryLoading, data: episodeInSeries } = useSeriesLookup(episodeId || undefined);
  const { data: nextItem } = useNextEpisode({ series, episodeId: episode?.mediaid });

  // UI
  const filters = useMemo(() => getFiltersFromSeries(series), [series]);
  const [activeSeason, setActiveSeason] = useState<string | undefined>(undefined);
  const hasRecommendations = Boolean(recommendations?.playlist?.length);

  // Season / episodes data
  const {
    data: episodes,
    isFetching: isEpisodesFetching,
    isLoading: isEpisodesLoading,
    fetchNextPage: fetchNextEpisodes,
    hasNextPage: hasNextEpisodesPage,
  } = useEpisodes(seriesId, activeSeason, { enabled: activeSeason !== undefined && !!series, usePlaceholderData: true });

  // User, entitlement
  const { isEntitled } = useEntitlement(episode);
  const episodesEntitlements = useEntitlement(episodes?.flatMap((episode) => episode.episodes));
  const areEpisodesEntitled = episodesEntitlements.reduce((acc, { mediaid, isEntitled }) => {
    return { ...acc, [mediaid!]: isEntitled };
  }, {} as { [mediaid: string]: boolean });
  const { isLoggedIn, hasSubscription, passes } = useAccountStore(
    ({ user, subscription, passes }) => ({ isLoggedIn: !!user, hasSubscription: !!subscription, passes }),
    shallow,
  );

  const episodeMetadata = useMemo(
    () =>
      episodeInSeries && {
        episodeNumber: typeof episodeInSeries.episode_number === 'number' ? String(episodeInSeries.episode_number) : episodeInSeries.episode_number || '',
        seasonNumber: typeof episodeInSeries.season_number === 'number' ? String(episodeInSeries.season_number) : episodeInSeries.season_number || '',
      },
    [episodeInSeries],
  );

  const episodesPlaylist = useMemo(
    () => ({
      title: data.title,
      description: data.description,
      feedid: series?.series_id,
      playlist: episodes?.flatMap((e) => e.episodes) || [],
    }),
    [data, series, episodes],
  );

  const season = useMemo(() => {
    return {
      value: activeSeason,
      year: firstEpisode?.year ? (firstEpisode.year as number) : firstEpisode?.pubdate ? new Date(firstEpisode.pubdate * 1000).getFullYear() : undefined,
      description: series?.seasons?.find((s) => String(s.season_number) === activeSeason)?.season_description,
    };
  }, [activeSeason, series, firstEpisode]);

  useEffect(() => {
    if (isSeriesDataLoading || isEpisodeLoading || isSeriesDictionaryLoading) {
      return;
    }

    // No episode is selected, filter is not set
    if (!episodeId && activeSeason === undefined) {
      setActiveSeason(filters[0]?.value || '');
      return;
    }

    // Select a corresponding season for an episode
    if (activeSeason === undefined && episodeMetadata) {
      setActiveSeason(episodeMetadata.seasonNumber);
    }
  }, [episodeMetadata, activeSeason, isSeriesDataLoading, isEpisodeLoading, isSeriesDictionaryLoading, filters, episodeId]);

  const seriesMetadata =
    (series?.season_count ?? 0) > 1
      ? { seasonsLabel: t('video:total_seasons', { count: series?.season_count }) }
      : { episodesLabel: t('video:total_episodes', { count: series?.episode_count }) };

  const primaryMetadata = [
    ...createVideoMetadata(data, {
      ...seriesMetadata,
      hoursAbbreviation: t('common:abbreviation.hours'),
      minutesAbbreviation: t('common:abbreviation.minutes'),
    }),
    t('video:series'),
  ];
  const videoFormats = createVideoFormatMetadata(data);

  const getCardSeriesUrl = (toSeries: PlaylistItem) => mediaURL({ id: toSeries.mediaid, title: toSeries.title });

  useEffect(() => {
    // Select first episode when
    // 1. there is no episode id in the URL (immediate)
    // 2. the given episode doesn't belong to the current series (only after dictionary has loaded to prevent multiple URL changes)
    if (((!isSeriesDictionaryLoading && !episodeInSeries) || !episodeId) && firstEpisode?.mediaid) {
      setEpisodeId(firstEpisode.mediaid);
    }
  }, [episodeId, episodeInSeries, firstEpisode, isSeriesDictionaryLoading]);

  const isLoading = isSeriesDataLoading || isSeriesDictionaryLoading || isEpisodeLoading || isFirstEpisodeLoading || !episodeId;

  return {
    series,
    episode,
    initialEpisodeId,
    isFirstEpisode: !!firstEpisode && episodeId === firstEpisode.mediaid,
    season,
    episodeMetadata,
    filters,
    setActiveSeason,
    playEpisode: episode,
    episodesPlaylist,
    recommendations,
    accessModel,
    nextItem,
    primaryMetadata,
    seriesMetadata,
    isRecommendationsLoading,
    isLoading,
    isEpisodesLoading,
    isEpisodesFetching,
    isEntitled,
    areEpisodesEntitled,
    isFavoritesEnabled,
    isLoggedIn,
    passes,
    hasSubscription,
    hasRecommendations,
    hasNextEpisodesPage,
    watchHistory: watchHistoryDictionary,
    videoFormats,
    fetchNextEpisodes,
    getCardSeriesUrl,
    setEpisodeId,
  };
}
